import React, { useContext, useLayoutEffect, useState } from 'react';
import Tooltip from '@material-ui/core/Tooltip';
import Button from 'Components/Atoms/Button';
import IconButton from 'Components/Atoms/IconButton';
import { CropFree } from 'Components/Atoms/Icons';
import { useReservationContext } from 'Contexts/ReservationContext';
import { RestaurantContext } from 'Contexts/RestaurantContext';
import './ReservationDoorToggle.scss';

/**
 * Below this width of the reservation header itself (not the window: the door pane takes
 * its share), the entry is an icon. The header's other actions already need about
 * 1 280 px; the labelled button adds about 110 px.
 */
export const LABELLED_FROM_PX = 1400;

/** Whether the surrounding navbar is too narrow for the labelled button; follows the bar, not the viewport. */
export function useNarrowHeader(anchor: HTMLElement | null) {
  const [narrow, setNarrow] = useState(false);
  useLayoutEffect(() => {
    const bar = anchor?.closest('.navbar') as HTMLElement | null;
    if (!bar) return undefined;
    const measure = () => setNarrow(bar.clientWidth < LABELLED_FROM_PX);
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(bar);
    return () => observer.disconnect();
  }, [anchor]);
  return narrow;
}

export default function ReservationDoorToggle({ disabled = false }: { restaurantId: string; date: string; disabled?: boolean }) {
  const { scanDoorOpen, toggleScanDoor } = useReservationContext();
  const { apps } = useContext(RestaurantContext);
  // A callback ref: the toggle may appear only once the restaurant's apps have loaded.
  const [anchor, setAnchor] = useState<HTMLSpanElement | null>(null);
  const narrow = useNarrowHeader(anchor);
  if (!apps?.includes('ticket_door') || !toggleScanDoor) return null;
  const open = !!scanDoorOpen;
  return (
    <span ref={setAnchor} className="reservation-door-toggle">
      {narrow ? (
        <Tooltip title={open ? 'Einlass schließen' : 'Einlass öffnen'}>
          {/* The wrapper keeps the tooltip on a disabled button. */}
          <span className="reservation-door-toggle-icon">
            <IconButton
              size="small"
              color={open ? 'primary' : 'textPrimary'}
              className={open ? 'is-active' : ''}
              aria-label="Einlass"
              aria-pressed={open}
              aria-controls="reservation-scan-workspace"
              disabled={disabled}
              onClick={toggleScanDoor}
            >
              <CropFree />
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <Button
          translation={null}
          variant={open ? 'primary' : 'outlined'}
          aria-pressed={open}
          aria-controls="reservation-scan-workspace"
          disabled={disabled}
          startIcon={(p) => <CropFree {...p} />}
          onClick={toggleScanDoor}
        >
          Einlass
        </Button>
      )}
    </span>
  );
}
